import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

// The six widgets negotiate format via `respond_to`, so every GET here needs both headers - a
// bare Accept: application/json falls back to [:html].
const JSON_HEADERS = {
  "X-Requested-With": "XMLHttpRequest",
  "Accept": "application/json, text/plain, */*"
}

const SERIES = { daily: "day", weekly: "week", monthly: "month" }

// Expanded to rgba at full alpha for the two line datasets' borders. Their backgroundColor is
// left undefined, which is inert since both render with fill: false.
const SENDS_BACKGROUND = "#EDE3FF"
const SENDS_BORDER = "#CAACFE"
const OPENS_BORDER = "#004F61"
const REDEMPTIONS_BORDER = "#00A338"

export default class extends Controller {
  static get targets() { return ["series", "dateRange", "canvas", "widget", "checkbox", "sentCount", "redeemedCount"] }
  static get values() { return { begin: String, end: String, import: Object } }

  connect() {
    this.urlParams = new URLSearchParams(window.location.search)
    this.defaultRange = this.beginValue.replaceAll("/", "") + "-" + this.endValue.replaceAll("/", "")
    this.currentParams = {
      series: this.urlParams.get("series") || SERIES.daily,
      range: this.urlParams.get("range") || this.defaultRange,
      sends: this.urlParams.get("sends") || "true",
      opens: this.urlParams.get("opens") || "true",
      redemptions: this.urlParams.get("redemptions") || "true",
      subjects: this.urlParams.get("subjects") || "",
      domains: this.urlParams.get("domains") || ""
    }
    this.urls = {
      chart: this.widgetUrl("chart"),
      subjects: this.widgetUrl("subjects"),
      domains: this.widgetUrl("domains"),
      collections: this.widgetUrl("collections"),
      opens: this.widgetUrl("opens"),
      redemptions: this.widgetUrl("redemptions")
    }

    this.onHideClick = this.onHideClick.bind(this)
    this.onPaginationClick = this.onPaginationClick.bind(this)
    this.onMainCountsClick = this.onMainCountsClick.bind(this)
    this.onMediaQueryChange = this.onMediaQueryChange.bind(this)

    this.element.addEventListener("click", this.onHideClick)
    this.element.addEventListener("click", this.onPaginationClick)
    this.element.addEventListener("click", this.onMainCountsClick)

    this.mediaQuery = window.matchMedia("(max-width: 768px)")
    this.mediaQuery.addEventListener("change", this.onMediaQueryChange)

    // The show/hide callbacks are plugin-announced through jQuery's own trigger (decisions.md D7),
    // so the binding stays jQuery; their bodies are plain DOM work and retreat to native.
    $(this.dateRangeTarget).
      on("show.daterangepicker", () => {
        document.querySelectorAll(".date-range-picker").forEach((el) => el.classList.add("is-open"))
      }).
      on("hide.daterangepicker", () => {
        document.querySelectorAll(".date-range-picker").forEach((el) => el.classList.remove("is-open"))
      })

    if (this.urlParams.get("new")) {
      $("#new-campaign-modal").modal("show")
    }
    this.setOptimalSeries(this.currentParams.range)
    this.updateWidgets()
    this.checkBoxes()

    // initialize expects a jQuery-wrapped element; this.element is a plain DOM node.
    window.ltDateRangePicker.initialize($(this.element), {
      buttonClasses: "btn btn-main btn-md",
      cancelButtonClasses: "btn-ghost"
    }, (newRange) => {
      this.setOptimalSeries(newRange, true)
      this.currentParams.range = newRange
      this.updateWidgets()
    })
    window.ltDateRangePicker.setPickerLabel()

    if (this.hasImportValue && this.importValue.stage) {
      const stage = this.importValue.stage
      if (stage === "waiting" || stage === "adding" || stage === "removing") {
        this.subscribeToImport(this.importValue.external_id)
      }
    }
  }

  disconnect() {
    this.element.removeEventListener("click", this.onHideClick)
    this.element.removeEventListener("click", this.onPaginationClick)
    this.element.removeEventListener("click", this.onMainCountsClick)
    this.mediaQuery.removeEventListener("change", this.onMediaQueryChange)
    $(this.dateRangeTarget).off("show.daterangepicker hide.daterangepicker")
    if (this.chart) { this.chart.destroy() }
  }

  // Reads the per-widget URL off its own `.widget` target -- a Values API candidate only if it
  // were on the controller's own root element; it is a per-target attribute, so it stays a plain
  // read (linter false positive, decisions.md D3/D8 shape).
  widgetUrl(id) {
    const element = this.widgetTargets.find((el) => el.id === id)
    return element ? element.dataset.url : undefined
  }

  // A checkbox toggle for a chart series. Bound directly on the three static checkboxes
  // (data-action="click->campaign-dashboard#toggleDataset"), not delegated, since they are never
  // replaced by ajax the way the widgets are.
  toggleDataset(event) {
    const checkbox = event.currentTarget
    const hidden = !checkbox.checked
    this.hideDataset(checkbox.dataset.id, hidden)
  }

  // Known quirk, pinned by the spec: currentParams stores this as a real boolean but renderChart
  // reads it back with a strict string comparison, so hiding a dataset does not survive the next
  // re-render from a period or date-range change - it comes back visible.
  hideDataset(chartId, hidden) {
    if (!this.chart) { return }

    this.chart.data.datasets.forEach((dataset) => {
      this.currentParams[chartId] = !hidden
      if (dataset.yAxisID === chartId) { dataset.hidden = hidden }
    })
    this.setUrl("")
    this.chart.update()
  }

  changeSeries() {
    this.currentParams.series = this.seriesTarget.value
    this.updateWidget("chart", this.setUrl(this.urls.chart))
  }

  // Delegated from the mount because these rows are replaced by ajax on every widget refresh.
  onHideClick(event) {
    const button = event.target.closest(".hide-domain, .hide-subject")
    if (!button) { return }

    event.preventDefault()
    const widget = button.closest(".widget")
    const widgetName = button.dataset.type

    const options = RequestHelper.defaultPostOptions()
    options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" })
    options.body = JSON.stringify({ [widgetName]: button.dataset[widgetName] })

    fetch(button.getAttribute("href"), options).
      then((response) => { this.updateWidget(widget.id, this.setUrl(this.urls[widget.id])) }).
      catch((response) => { console.warn(response) })
  }

  // Bound directly on the two static checkboxes (data-action="change->campaign-dashboard#onShowHiddenChange"),
  // not delegated -- unlike onHideClick's targets, these checkboxes are never replaced by ajax.
  onShowHiddenChange(event) {
    const checkbox = event.currentTarget

    const widget = checkbox.closest(".hideable-items").querySelector(".widget")
    const pagination = widget.querySelectorAll("tr.paginator ul a")
    const isChecked = checkbox.checked
    const type = checkbox.dataset.type

    if (isChecked) {
      this.currentParams[type] = "visible"
    } else {
      delete this.currentParams[type]
    }

    // Known bug, long-standing: `href.split("?")[1]` keeps only the old query string, not the
    // path, then appends a second "?" and a new one, producing a malformed href. Only reachable
    // by combining Show Hidden Data with pagination.
    pagination.forEach((link) => {
      const path = link.getAttribute("href").split("?")[1]
      const searchParams = new URLSearchParams(this.currentParams)
      searchParams.set(type, isChecked ? "visible" : "")
      if (this.mediaQuery.matches) { searchParams.set("mobile", "true") }
      link.setAttribute("href", `${path}?${searchParams.toString()}`)
    })

    this.updateWidget(widget.id, this.setUrl(this.urls[type]))
  }

  onPaginationClick(event) {
    const link = event.target.closest(".widget ul.pagination a:not([disabled])")
    if (!link) { return }

    event.preventDefault()
    const widget = link.closest(".widget")
    this.updateWidget(widget.id, link.getAttribute("href"))
  }

  onMainCountsClick(event) {
    const link = event.target.closest("a.campaign-main-counts")
    if (!link) { return }

    event.preventDefault()
    const destination = `${link.getAttribute("href")}&range=${this.currentParams.range}`
    // Asserted same-origin per the S3 rule before navigating, even though the href always comes
    // off our own server-rendered markup.
    if (RequestHelper.isSameOriginUrl(destination)) {
      window.location.href = destination
    }
  }

  onMediaQueryChange() {
    this.updateWidgets()
  }

  updateWidgets() {
    this.widgetTargets.forEach((element) => {
      let url = this.setUrl(this.urls[element.id])
      if (this.mediaQuery.matches) { url = `${url}&mobile=true` }
      this.updateWidget(element.id, url)
    })
  }

  // No loading state: the previous content stays until the response replaces it. The old
  // implementation looked up a loading template that was never registered, so it never showed one
  // either.
  updateWidget(id, url) {
    if (!url) { return }

    const options = RequestHelper.defaultRequestOptions()
    options.headers = Object.assign({}, options.headers, JSON_HEADERS)

    fetch(url, options).
      then((response) => {
        if (!response.ok) { throw new Error(`Widget request failed (${response.status})`) }
        return response.json()
      }).
      then((data) => {
        Object.keys(data).forEach((key) => {
          if (key !== "html") { data[key] = JSON.parse(data[key]) }
        })
        if (id === "chart") {
          this.renderChart(data)
        } else {
          // data.html is always render_to_string of one of our own
          // subdomain/campaigns/widgets/* partials (Subdomain::CampaignsController#render_widget /
          // #render_overview_widget) -- server-rendered from our own origin, not from user input.
          this.widgetTargets.find((el) => el.id === id).innerHTML = data.html
        }
      }).
      catch((response) => { console.warn(response) })
  }

  /* Renders the chart data by calling Chart.js directly, replacing angular-chart.js's chart-bar
     directive (D7/D8: rendering only, safe to migrate independently of the other twelve views
     still using that directive). Rebuilt from scratch on every refresh rather than incrementally
     updated, matching the directive's own watcher-driven destroy+recreate (patterns.md). */
  renderChart(data) {
    let labels = Object.keys(data.sends)
    if (this.currentParams.series === SERIES.weekly) {
      labels = labels.map((part) => `  Week of ${part}  `)
    }

    const maxTicks = 10
    const maxSize = this.maxYAxisValue(data)
    const stepSize = Math.ceil(maxSize / maxTicks)

    const datasets = [
      {
        label: "Sends", yAxisID: "sends",
        hidden: this.currentParams.sends === "false",
        borderWidth: 1, backgroundColor: SENDS_BACKGROUND, borderColor: SENDS_BORDER,
        barThickness: 38, order: 3,
        data: Object.values(data.sends)
      },
      {
        type: "line", label: "Opens", fill: false, yAxisID: "opens",
        hidden: this.currentParams.opens === "false",
        pointBorderWidth: 3, pointRadius: 7, borderWidth: 5, pointBorderColor: "#fff",
        borderColor: OPENS_BORDER, order: 2,
        data: Object.values(data.opens)
      },
      {
        type: "line", label: "Redemptions", fill: false, yAxisID: "redemptions",
        hidden: this.currentParams.redemptions === "false",
        pointBorderWidth: 3, pointRadius: 7, borderWidth: 5, pointBorderColor: "#fff",
        borderColor: REDEMPTIONS_BORDER, order: 1,
        data: Object.values(data.redemptions)
      }
    ]

    if (this.chart) { this.chart.destroy() }

    this.chart = new Chart(this.canvasTarget, {
      type: "bar",
      data: { labels, datasets },
      options: {
        aspectRatio: 5,
        legend: { display: false },
        tooltips: {
          callbacks: {
            title: (tooltipItems) => this.chart.data.labels[tooltipItems[0].index]
          }
        },
        scales: {
          yAxes: [
            { id: "sends", ticks: { display: false, beginAtZero: true, maxTicksLimit: maxTicks, max: maxSize, stepSize } },
            {
              id: "opens",
              ticks: { display: false, beginAtZero: true, maxTicksLimit: maxTicks, max: maxSize, stepSize },
              gridLines: { drawTicks: false, display: false, drawBorder: false }
            },
            {
              id: "redemptions", offset: false,
              ticks: { beginAtZero: true, maxTicksLimit: maxTicks, max: maxSize, stepSize },
              gridLines: { color: "#F6EFE1", drawBorder: false, borderDash: [17, 17], zeroLineColor: "#00A338" }
            }
          ],
          xAxes: [{
            gridLines: { color: "#F6EFE1", borderDash: [17, 17], zeroLineWidth: 0, drawBorder: false },
            ticks: {
              maxRotation: 0,
              maxTicksLimit: this.currentParams.series === SERIES.weekly ? 5 : 10,
              fontFamily: "'Söhne', sans-serif",
              fontSize: 14
            }
          }]
        }
      }
    })

    this.renderOverviewBoxes(data.sent_count, data.redeemed_count)
  }

  renderOverviewBoxes(sent, redeemed) {
    this.sentCountTarget.textContent = sent
    this.redeemedCountTarget.textContent = redeemed
  }

  /* Normalize the values by defaulting to something smooth with a max value divisible by 10 */
  maxYAxisValue(data) {
    const valuesArray = []
    for (const property in data) {
      Object.values(data[property]).forEach((number) => { valuesArray.push(number) })
    }

    let maxValue = Math.max(...valuesArray)
    let scale = 10.0

    if (maxValue < 5) { return maxValue + 1 }
    if (maxValue < 8) { return maxValue + 2 }
    if (maxValue < 10) { return 10 }

    maxValue = maxValue < 10 ? 10 : maxValue

    while (Math.ceil(maxValue / scale) % 5 !== 0) {
      scale += 10.0
      if (scale > 100) { scale = 10; break }
    }

    return Math.ceil(maxValue / scale) * scale
  }

  /* Returns the series that makes sense based on the number of data points requested in the date
     range */
  setOptimalSeries(range, force = false) {
    if (this.urlParams.get("series") !== null && !force) { return }

    const startDate = moment(range.split("-")[0], "MMDDYYYY")
    const endDate = moment(range.split("-")[1], "MMDDYYYY")
    const daysBetween = endDate.diff(startDate, "days") + 1
    const maxDaysForWeekly = 62
    const maxDaysForMonthly = 434
    let selectedSeries = SERIES.daily

    if (daysBetween >= maxDaysForWeekly && daysBetween < maxDaysForMonthly) {
      selectedSeries = SERIES.weekly
    } else if (daysBetween >= maxDaysForMonthly) {
      selectedSeries = SERIES.monthly
    }

    this.seriesTarget.value = selectedSeries
    this.currentParams.series = selectedSeries
    this.setUrl("")
  }

  /* Sets the address bar up with the right query params, and returns the URL with correct
     parameters */
  setUrl(url) {
    const paramsString = `?${$.param(this.currentParams)}`
    window.history.replaceState(this.currentParams, null, paramsString)
    return `${url}${paramsString}`
  }

  /* Sets any checkboxes referred to in the URL as checked if necessary. */
  checkBoxes() {
    for (const param in this.currentParams) {
      const trueOrVisible = this.currentParams[param] === "true" || this.currentParams[param] === "visible"
      const checkbox = this.checkboxTargets.find((el) => el.dataset.id === param)
      if (checkbox) { checkbox.checked = trueOrVisible }
    }
  }

  subscribeToImport(campaignId) {
    const modal = document.getElementById("dashboard-updating-modal")
    $(modal).modal({ backdrop: "static", show: true })

    this.importSubscription = App.cable.subscriptions.create({ channel: "CampaignImportsChannel", id: campaignId }, {
      received: (data) => {
        const stage = data.campaign.stage
        const progressBar = modal.querySelector(".progress-bar")
        const countLabel = modal.querySelector(".count-label")
        const descriptionLabel = modal.querySelector(".description")
        // In case subscription messages come in a weird order, we keep the highest progress
        const currentProgress = parseInt(progressBar.getAttribute("aria-valuenow"))
        const newProgress = currentProgress > data.campaign.progress ? currentProgress : data.campaign.progress

        if (stage === "adding" || stage === "removing") {
          progressBar.style.width = `${newProgress}%`
          // Both labels are plain text (no markup) both server-side (updating_modal partial's
          // initial render) and here -- textContent instead of innerHTML clears the S1 finding
          // with no rendering difference.
          descriptionLabel.textContent = data.campaign.count_description
          countLabel.textContent = `${data.campaign.current_count}/${data.campaign.total_count}`
        } else if (stage === "complete") {
          this.updateWidgets()
          $(".modal").modal("hide")
          this.importSubscription.unsubscribe()
        }
      }
    })
  }
}
